import {
    Box,
    Button,
    capitalize,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Stack,
    Switch,
    TextField,
} from "@mui/material";
import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {Prompt} from "../../api";
import {useActivePromptByAufgabeId} from "../../client/queries";
import {useAufgabenPromptHandling} from "../../hooks/aufgaben/useAufgabenPromptHandling";
import {DEFAULT_LANGUAGE} from "../../config/language";
import {UseMutationResult} from "react-query";
import type {PromptDataShape} from "./AufgabePromptVersionEditor";

interface Props {
    open: boolean;
    onClose: () => void;
    /** Entity ID — aufgabeId or phoneNumberId. Only used to load fallback seed prompt when seedPrompt is not provided. */
    entityId: string;
    sprache?: string;
    createMutation?: UseMutationResult<Prompt, unknown, {data: PromptDataShape; activate: boolean}>;
    seedPrompt?: Prompt;
    onCreated?: (prompt: Prompt) => void;
    /** If true, skip the useActivePromptByAufgabeId query (used for non-Aufgabe contexts). */
    skipActivePromptQuery?: boolean;
}

export default function DialogCreatePromptVersion({open, onClose, entityId, sprache, createMutation: externalCreateMutation, seedPrompt, onCreated, skipActivePromptQuery}: Props) {
    const {t: translation} = useTranslation('aufgaben');
    const {t: tLang} = useTranslation('sprachen');
    const active: Prompt | undefined = useActivePromptByAufgabeId(entityId, {enabled: !skipActivePromptQuery});
    const {createPromptMutation: internalCreateMutation} = useAufgabenPromptHandling(entityId);
    const createPromptMutation = externalCreateMutation ?? internalCreateMutation;
    const seed = seedPrompt ?? active;

    const [versionName, setVersionName] = useState('');
    const [versionDescription, setVersionDescription] = useState('');
    const [prompt, setPrompt] = useState('');
    const [foreignLanguagePrompt, setForeignLanguagePrompt] = useState('');
    const [activate, setActivate] = useState(true);

    useEffect(() => {
        if (open) {
            setVersionName('');
            setVersionDescription('');
            setPrompt(seed?.prompt ?? '');
            setForeignLanguagePrompt(seed?.foreignLanguagePrompt ?? '');
            setActivate(true);
        }
    }, [open, seed]);

    const lang = sprache ?? DEFAULT_LANGUAGE;
    const showForeign = lang !== DEFAULT_LANGUAGE && lang.length === 5;

    const handleSubmit = async () => {
        try {
            const created = await createPromptMutation.mutateAsync({
                data: {
                    versionName: versionName.trim(),
                    versionDescription: versionDescription || undefined,
                    prompt: prompt,
                    foreignLanguagePrompt: foreignLanguagePrompt || undefined,
                },
                activate,
            });
            onCreated?.(created);
            onClose();
        } catch {
            // error already surfaced via snackbar in the mutation hook
        }
    };

    const canSubmit = versionName.trim().length > 0 && !createPromptMutation.isLoading;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{capitalize(translation('prompt-version-create-dialog-title'))}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{pt: 1}}>
                    <Box display="grid" gridTemplateColumns={{xs: '1fr', md: '1fr 2fr'}} gap={2}>
                        <TextField
                            label={capitalize(translation('prompt-version-name'))}
                            value={versionName}
                            onChange={(e) => setVersionName(e.target.value)}
                            required
                            autoFocus
                            size="small"
                        />
                        <TextField
                            label={capitalize(translation('prompt-version-description'))}
                            value={versionDescription}
                            onChange={(e) => setVersionDescription(e.target.value)}
                            size="small"
                        />
                    </Box>
                    <Box display="grid" gridTemplateColumns={showForeign ? {xs: '1fr', md: '1fr 1fr'} : '1fr'} gap={2}>
                        <TextField
                            label={capitalize(translation('task-attributes-prompt'))}
                            value={prompt}
                            onChange={(e) => setPrompt(e.target.value)}
                            multiline
                            rows={16}
                            fullWidth
                        />
                        {showForeign && (
                            <TextField
                                label={`${capitalize(translation('task-attributes-prompt'))} (${tLang(lang)})`}
                                value={foreignLanguagePrompt}
                                onChange={(e) => setForeignLanguagePrompt(e.target.value)}
                                multiline
                                rows={16}
                                fullWidth
                            />
                        )}
                    </Box>
                    <FormControlLabel
                        control={<Switch checked={activate} onChange={(e) => setActivate(e.target.checked)} color="primary"/>}
                        label={capitalize(translation('prompt-version-create-activate'))}
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{capitalize(translation('abort'))}</Button>
                <Button variant="contained" color="secondary" onClick={handleSubmit} disabled={!canSubmit}>
                    {capitalize(translation('prompt-version-create'))}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
